/**
 * FERP Portal - Main Layout Component
 *
 * Implements the application shell with:
 * - Header with navigation and user menu
 * - Sidebar with DATA-DRIVEN navigation from SubgraphMetadata
 * - Main content area
 *
 * FERP Principles:
 * - Data-driven: Navigation items come from backend (SubgraphMetadata)
 * - NO hardcoded navigation - ALL nav items from backend
 * - Frontend is DUMB - only displays what backend defines
 *
 * CONSTRAINTS:
 * - Mantine v8.x only (NO MUI)
 * - View layer determines available navigation
 */

import {
  AppShell,
  Group,
  Burger,
  Title,
  NavLink,
  ScrollArea,
  Avatar,
  Menu,
  UnstyledButton,
  Text,
  Stack,
  Badge,
  Divider,
  ActionIcon,
  useMantineColorScheme,
  Skeleton,
  Tooltip,
  Box,
  Drawer,
  Center,
  Loader,
} from '@mantine/core';
import React from 'react';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import {
  IconLogout,
  IconSun,
  IconMoon,
  IconChevronDown,
  IconBell,
  IconUser,
  IconSearch,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconPlus,
  IconSitemap,
  IconMessageChatbot,
} from '@tabler/icons-react';
import { spotlight } from '@mantine/spotlight';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth, useViewLayer, type ViewLayer } from '@/auth/AuthContext';
import { isMockAuthEnabled, isDevModeEnabled } from '@/utils/env';
import { useFeatureFilteredConfig } from '@/hooks/useFeatureFilteredConfig';
import { type ViewLayer as ConfigViewLayer } from '@/hooks/useTenantSubgraphConfig';
import { getIcon } from '@/utils/iconMap';
import { useUserMenuItems } from '@/hooks/useUserMenuItems';
import { usePresenceHeartbeat } from '@ferp/workflow-forms';
import { GlobalSearchPalette } from '@/components/GlobalSearchPalette';
import { FloatingOrchestrationMenu } from '@/components/workflow-orchestration';
import { AuthDebugPanel, DevImpersonationPanel, PermissionDebugPanel, ScopeGapsPanel, ViewContextPanel } from '@/components/DevTools';
import { WallIndicator } from '@/components/WallIndicator';
import { FocusContextProvider } from '@/contexts/FocusContext';
import { FocusBanner } from '@/components/shared/FocusBanner';
import { ContextBanner } from '@/components/shared/ContextBanner';
import { PRIMITIVE_METADATA } from '@/config/primitive-metadata';
import { SidebarEntityCreatePanel } from '@/components/SidebarEntityCreatePanel';
import { AgentAssistPanel } from '@/components/AgentAssistPanel';
import { AssistantChatPanel } from '@/components/AssistantChatPanel';
import { NotificationBadge } from '@/components/notifications';

// ---------------------------------------------------------------------------
// Quick-create: map nav item paths → { subgraph, entityType }
// ---------------------------------------------------------------------------

/** Reverse-lookup: /ferp-core/{slug} → GraphQL entity type name */
const PRIMITIVE_PATH_TO_ENTITY: Record<string, string> = Object.entries(PRIMITIVE_METADATA).reduce(
  (acc, [type, meta]) => {
    acc[meta.path] = type.charAt(0).toUpperCase() + type.slice(1);
    return acc;
  },
  {} as Record<string, string>,
);

interface QuickCreateTarget {
  subgraph: string;
  entityType: string;
  label: string;
}

/**
 * Parse a nav item path into a quick-create target.
 * Returns null for group parents or non-entity paths.
 */
function parseNavForCreate(path: string, label: string): QuickCreateTarget | null {
  const pathOnly = path.split('?')[0] ?? path;

  // Primitive: /ferp-core/{slug}
  const primitiveEntityType = PRIMITIVE_PATH_TO_ENTITY[pathOnly];
  if (primitiveEntityType) {
    // Seed file uses "core" not "ferp-core" — must match FormSchemaRepository lookup key
    return { subgraph: 'core', entityType: primitiveEntityType, label };
  }

  // Subgraph entity: /admin/{subgraph}?entity={EntityType}
  try {
    const url = new URL(path, 'http://x');
    const segs = url.pathname.split('/').filter(Boolean);
    if ((segs[0] === 'admin' || segs[0] === 'architect') && segs[1]) {
      const entityType = url.searchParams.get('entity');
      if (entityType) return { subgraph: segs[1], entityType, label };
    }
  } catch {
    // ignore malformed paths
  }

  return null;
}

interface MainLayoutProps {
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// QuickCreateButton — extracted to keep renderFullNav under complexity limit
// ---------------------------------------------------------------------------

interface QuickCreateButtonProps {
  target: QuickCreateTarget;
  currentEntityType: string | undefined;
  onActivate: (target: QuickCreateTarget) => void;
}

function QuickCreateButton({ target, currentEntityType, onActivate }: Readonly<QuickCreateButtonProps>) {
  const isActive = currentEntityType === target.entityType;
  return (
    <Tooltip label={`Create ${target.label}`} withArrow position="right">
      <ActionIcon
        size="xs"
        variant={isActive ? 'filled' : 'subtle'}
        color={isActive ? 'ferpBlue' : undefined}
        visibleFrom="sm"
        onClick={(e) => { e.stopPropagation(); onActivate(target); }}
        aria-label={`Create ${target.label}`}
      >
        <IconPlus size={12} />
      </ActionIcon>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// NavChildItem — extracted to keep renderFullNav under complexity limit
// ---------------------------------------------------------------------------

interface NavChildItemProps {
  child: { id: string; label: string; icon: React.FC<{ size?: number }>; path: string };
  activePath: string;
  quickCreateEntityType: string | undefined;
  onNavigate: (path: string) => void;
  onQuickCreate: (target: QuickCreateTarget) => void;
}

function NavChildItem({ child, activePath, quickCreateEntityType, onNavigate, onQuickCreate }: Readonly<NavChildItemProps>) {
  const childTarget = parseNavForCreate(child.path, child.label);
  const isActive = activePath === child.path || activePath.startsWith(`${child.path}/`);
  return (
    <NavLink
      label={child.label}
      leftSection={child.icon ? <child.icon size={16} /> : null}
      rightSection={childTarget && (
        <QuickCreateButton
          target={childTarget}
          currentEntityType={quickCreateEntityType}
          onActivate={onQuickCreate}
        />
      )}
      active={isActive}
      onClick={() => onNavigate(child.path)}
      variant="filled"
    />
  );
}

// ---------------------------------------------------------------------------
// Type alias for processed nav items (icon resolved via getIcon)
// ---------------------------------------------------------------------------

type NavItem = {
  id: string;
  label: string;
  icon: React.FC<{ size?: number }>;
  path: string;
  badge: { type: string; value?: string; color: string } | null;
  parentId: string | null;
};

// ---------------------------------------------------------------------------
// Module-level computed values — keep out of MainLayout to reduce complexity
// ---------------------------------------------------------------------------

function getSafetyColor(level: string | undefined): string {
  if (level === 'CRITICAL') return 'red';
  if (level === 'HIGH') return 'orange';
  if (level === 'MEDIUM') return 'yellow';
  return 'green';
}

function getNavPadding(isCreateOpen: boolean, isCollapsed: boolean): string | number {
  if (isCreateOpen) return 0;
  if (isCollapsed) return 'xs';
  return 'md';
}

function getNavWidth(isCreateOpen: boolean, isCollapsed: boolean): number | { base: number; sm: number } {
  if (isCreateOpen) return { base: 280, sm: 700 };
  if (isCollapsed) return { base: 280, sm: 80 };
  return 280;
}

// ---------------------------------------------------------------------------
// FullNavItems — expanded nav for mobile + desktop. Extracted from MainLayout
// to keep MainLayout's cognitive complexity below the 15-point limit.
// ---------------------------------------------------------------------------

interface FullNavItemsProps {
  items: NavItem[];
  childrenByParent: Record<string, NavItem[]>;
  activePath: string;
  configLoading: boolean;
  quickCreateEntityType: string | undefined;
  setQuickCreate: React.Dispatch<React.SetStateAction<QuickCreateTarget | null>>;
  navigate: (path: string) => void;
}

function FullNavItems({
  items, childrenByParent, activePath, configLoading,
  quickCreateEntityType, setQuickCreate, navigate,
}: Readonly<FullNavItemsProps>) {
  const handleQuickCreate = (target: QuickCreateTarget) => {
    setQuickCreate((prev) => prev?.entityType === target.entityType ? null : target);
  };
  const handleNavigate = (path: string) => { setQuickCreate(null); navigate(path); };

  // Accordion: find which parent contains the active path on mount
  const initialOpenId = React.useMemo(() => {
    for (const item of items) {
      const kids = childrenByParent[item.id];
      const isMyWork = item.id === 'nav-grp-mywork' || item.id === 'nav-grp-arch-mywork';
      if (isMyWork && kids?.length) return item.id;
      if (kids?.some((c) => activePath === c.path || activePath.startsWith(`${c.path}/`))) return item.id;
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on mount

  // Only one parent open at a time — prevents endless scroll on large subgraphs
  const [openedId, setOpenedId] = React.useState<string | null>(initialOpenId);

  if (configLoading) {
    return (
      <>
        <Skeleton height={36} radius="sm" mb={4} />
        <Skeleton height={36} radius="sm" mb={4} />
        <Skeleton height={36} radius="sm" mb={4} />
        <Skeleton height={20} width="40%" radius="sm" mt="md" mb={4} />
        <Skeleton height={36} radius="sm" mb={4} />
        <Skeleton height={36} radius="sm" mb={4} />
      </>
    );
  }

  return items.map((item) => {
    const itemChildren = childrenByParent[item.id];
    const hasChildren = itemChildren && itemChildren.length > 0;
    const topLevelTarget = hasChildren ? null : parseNavForCreate(item.path, item.label);
    const isLeafActive = !hasChildren && (activePath === item.path || activePath.startsWith(`${item.path}/`));
    const isChildActive = hasChildren && itemChildren.some(
      (c) => activePath === c.path || activePath.startsWith(`${c.path}/`)
    );
    const isOpen = openedId === item.id;
    return (
      <NavLink
        key={item.id}
        label={item.label}
        leftSection={item.icon ? <item.icon size={18} /> : null}
        rightSection={topLevelTarget && (
          <QuickCreateButton target={topLevelTarget} currentEntityType={quickCreateEntityType} onActivate={handleQuickCreate} />
        )}
        active={isLeafActive || isChildActive}
        opened={hasChildren ? isOpen : undefined}
        onChange={hasChildren ? () => setOpenedId(isOpen ? null : item.id) : undefined}
        onClick={hasChildren ? undefined : () => handleNavigate(item.path)}
        variant="filled"
      >
        {itemChildren?.map((child) => (
          <NavChildItem
            key={child.id}
            child={child}
            activePath={activePath}
            quickCreateEntityType={quickCreateEntityType}
            onNavigate={handleNavigate}
            onQuickCreate={handleQuickCreate}
          />
        ))}
      </NavLink>
    );
  });
}

// ---------------------------------------------------------------------------
// CollapsedNavItems — icon-only nav for collapsed sidebar + create-panel strip.
// ---------------------------------------------------------------------------

interface CollapsedNavItemsProps {
  items: NavItem[];
  childrenByParent: Record<string, NavItem[]>;
  activePath: string;
  configLoading: boolean;
  navigate: (path: string) => void;
  drilledId?: string | null;
  onExpand?: () => void;
}

function CollapsedNavItems({
  items, childrenByParent, activePath, configLoading, navigate, drilledId, onExpand,
}: Readonly<CollapsedNavItemsProps>) {
  if (configLoading) {
    return (
      <>
        <Skeleton height={40} width={40} radius="sm" />
        <Skeleton height={40} width={40} radius="sm" />
        <Skeleton height={40} width={40} radius="sm" />
      </>
    );
  }
  return items.map((item) => {
    const itemChildren = childrenByParent[item.id];
    const hasChildren = itemChildren && itemChildren.length > 0;
    const isActive = !hasChildren && (activePath === item.path || activePath.startsWith(`${item.path}/`));
    const isChildActive = itemChildren?.some(
      (child) => activePath === child.path || activePath.startsWith(`${child.path}/`)
    );
    if (hasChildren) {
      // Active drilled section: clicking re-expands the sidebar to show submenu
      if (drilledId === item.id && onExpand) {
        return (
          <Tooltip key={item.id} label={`Expand ${item.label}`} position="right" withArrow>
            <ActionIcon
              variant="filled"
              color="ferpBlue"
              size="xl"
              style={{ width: '100%' }}
              onClick={onExpand}
              aria-label={`Expand ${item.label}`}
            >
              {item.icon ? <item.icon size={20} /> : null}
            </ActionIcon>
          </Tooltip>
        );
      }
      return (
        <Menu key={item.id} position="right-start" withArrow shadow="md" trigger="hover" openDelay={100} closeDelay={200}>
          <Menu.Target>
            <ActionIcon
              variant={isChildActive ? 'light' : 'subtle'}
              color={isChildActive ? 'ferpBlue' : undefined}
              size="xl"
              style={{ width: '100%' }}
            >
              {item.icon ? <item.icon size={20} /> : null}
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{item.label}</Menu.Label>
            {itemChildren?.map((child) => (
              <Menu.Item
                key={child.id}
                leftSection={child.icon ? <child.icon size={16} /> : null}
                onClick={() => navigate(child.path)}
                color={
                  activePath === child.path || activePath.startsWith(`${child.path}/`)
                    ? 'ferpBlue' : undefined
                }
              >
                {child.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      );
    }
    return (
      <Tooltip key={item.id} label={item.label} position="right" withArrow>
        <ActionIcon
          variant={isActive ? 'filled' : 'subtle'}
          color={isActive ? 'ferpBlue' : undefined}
          size="xl"
          onClick={() => navigate(item.path)}
          style={{ width: '100%' }}
        >
          {item.icon ? <item.icon size={20} /> : null}
        </ActionIcon>
      </Tooltip>
    );
  });
}

// ---------------------------------------------------------------------------
// FullNavDrawer — full-screen left drawer showing complete nav hierarchy
// ---------------------------------------------------------------------------

interface FullNavDrawerProps {
  opened: boolean;
  onClose: () => void;
  items: NavItem[];
  childrenByParent: Record<string, NavItem[]>;
  activePath: string;
  navigate: (path: string) => void;
}

function FullNavDrawer({ opened, onClose, items, childrenByParent, activePath, navigate }: Readonly<FullNavDrawerProps>) {
  const handleNav = (path: string) => { navigate(path); onClose(); };
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={<Text fw={700} size="sm" tt="uppercase" c="dimmed">All Navigation</Text>}
      position="left"
      size="sm"
      overlayProps={{ backgroundOpacity: 0.35, blur: 2 }}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap={2}>
        {items.map((item) => {
          const kids = childrenByParent[item.id];
          const hasKids = kids && kids.length > 0;
          const isChildActive = hasKids && kids.some(
            (c) => activePath === c.path || activePath.startsWith(`${c.path}/`)
          );
          const isLeafActive = !hasKids && (activePath === item.path || activePath.startsWith(`${item.path}/`));
          return (
            <NavLink
              key={item.id}
              label={item.label}
              leftSection={item.icon ? <item.icon size={18} /> : null}
              active={isLeafActive || isChildActive}
              defaultOpened={isChildActive}
              onClick={hasKids ? undefined : () => handleNav(item.path)}
              variant="filled"
            >
              {kids?.map((child) => (
                <NavLink
                  key={child.id}
                  label={child.label}
                  leftSection={child.icon ? <child.icon size={16} /> : null}
                  active={activePath === child.path || activePath.startsWith(`${child.path}/`)}
                  onClick={() => handleNav(child.path)}
                  variant="filled"
                />
              ))}
            </NavLink>
          );
        })}
      </Stack>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert ViewLayer from AuthContext to ConfigViewLayer for hook.
 */
function toConfigViewLayer(layer: ViewLayer): ConfigViewLayer {
  return layer.charAt(0).toUpperCase() + layer.slice(1) as ConfigViewLayer;
}

// View layer labels and colors
const viewLayerConfig: Record<ViewLayer, { label: string; color: string }> = {
  user: { label: 'User', color: 'blue' },
  admin: { label: 'Admin', color: 'orange' },
  architect: { label: 'Architect', color: 'violet' },
};

export function MainLayout({ children }: Readonly<MainLayoutProps>) {
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopCollapsed, setDesktopCollapsed] = useLocalStorage<boolean>({
    key: 'ferp-nav-collapsed',
    defaultValue: false,
  });
  const toggleDesktop = () => setDesktopCollapsed((prev) => !prev);

  // Quick-create: expand sidebar and show inline create form
  const [quickCreate, setQuickCreate] = React.useState<QuickCreateTarget | null>(null);

  // Full nav drawer: complete hierarchy overlay
  const [navDrawerOpen, { open: openNavDrawer, close: closeNavDrawer }] = useDisclosure(false);

  // AI panels — DRR-187 (intake) + DRR-131 (executive assistant)
  const [agentAssistOpen, { open: openAgentAssist, close: closeAgentAssist }] = useDisclosure(false);
  const [assistantChatOpen, { open: openAssistantChat, close: closeAssistantChat }] = useDisclosure(false);

  const { user, tenant, logout, isLoading: authLoading } = useAuth();
  const viewLayer = useViewLayer();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  // DRR-061: Presence heartbeat (sends ONLINE/AWAY to backend)
  // Skip in mock auth mode — no FERP Core to receive presence updates
  const isMockAuth = typeof window !== 'undefined'
    && (window as unknown as { __ENV__?: Record<string, string> }).__ENV__?.ENABLE_MOCK_AUTH === 'true';
  usePresenceHeartbeat({ skip: !user || isMockAuth });

  // DATA-DRIVEN: Get ALL navigation from backend configuration (including core items)
  // Uses useFeatureFilteredConfig to respect Feature Catalog layer overrides
  const { loading: configLoading, getNavItemsForLayer } = useFeatureFilteredConfig(tenant?.id ?? null);

  // Backend-driven user menu items (avatar dropdown)
  // From Setting YAML → scope-filtered → NVO-customized
  const { items: userMenuItems } = useUserMenuItems();

  // Get all navigation items for current layer from backend (DATA-DRIVEN)
  const configViewLayer = toConfigViewLayer(viewLayer);
  const backendNavItems = getNavItemsForLayer(configViewLayer);

  // Convert backend items to frontend format, grouping children under parents
  const allItems: NavItem[] = backendNavItems.map((item) => ({
    id: item.id,
    label: item.label,
    icon: getIcon(item.icon) as NavItem['icon'],
    path: item.path,
    badge: item.badge,
    parentId: item.parentId,
  }));
  const topLevelItems = allItems.filter((item) => !item.parentId);
  const childItems = allItems.filter((item) => item.parentId);
  const childrenByParent = childItems.reduce<Record<string, NavItem[]>>((acc, item) => {
    const pid = item.parentId!;
    (acc[pid] ??= []).push(item);
    return acc;
  }, {});

  const viewConfig = viewLayerConfig[viewLayer];
  const safetyColor = getSafetyColor(tenant?.safetyLevel);
  // Computed before JSX so TypeScript doesn't narrow it inside ternary branches
  const activeQuickCreateType: string | undefined = quickCreate?.entityType;

  // DRR-997: Wait for auth to fully initialize before rendering layout.
  // After OIDC callback, tenant may be null briefly while token is processed.
  // Without this check, nav renders empty and never re-renders when tenant loads.
  if (authLoading || (user && !tenant)) {
    return (
      <Center h="100vh">
        <Stack ta="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text size="sm" c="dimmed">Loading workspace...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: getNavWidth(!!quickCreate, desktopCollapsed),
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened },
      }}
      padding="md"
      transitionDuration={200}
      transitionTimingFunction="ease"
    >
      {/* Header */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Title order={3} c="ferpBlue">FERP Portal</Title>
            {tenant && (
              <Badge variant="light" color="gray" size="sm">
                {tenant.name}
              </Badge>
            )}
            {isMockAuthEnabled() && user && (
              <Badge variant="light" color="blue" size="sm">
                {user.displayName}
              </Badge>
            )}
          </Group>

          <Group>
            {/* View Layer Badge — role-locked, no manual switching (DRR-102) */}
            <Badge color={viewConfig.color} variant="filled">
              {viewConfig.label} View
            </Badge>

            {/* DRR-123: Information Barrier Wall Indicator */}
            <WallIndicator />

            {/* AI Assistant — single entry point for DRR-187 (intake) + DRR-131 (questions) */}
            <Tooltip label="AI Assistant" position="bottom">
              <ActionIcon
                variant="light"
                color="grape"
                size="lg"
                aria-label="AI Assistant"
                onClick={openAssistantChat}
              >
                <IconMessageChatbot size={18} />
              </ActionIcon>
            </Tooltip>

            {/* Notifications Bell with unread count badge (DRR-233) */}
            <NotificationBadge inboxPath="/ferp-core/inbox" />

            {/* Global Search (Cmd+K) */}
            <ActionIcon
              variant="light"
              size="lg"
              aria-label="Search (Cmd+K)"
              onClick={() => spotlight.open()}
            >
              <IconSearch size={18} />
            </ActionIcon>

            {/* Theme Toggle */}
            <ActionIcon
              variant="light"
              onClick={() => toggleColorScheme()}
              size="lg"
              aria-label="Toggle color scheme"
            >
              {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>

            {/* User Menu */}
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs">
                    <Avatar
                      color="ferpBlue"
                      radius="xl"
                      size="sm"
                    >
                      {user?.displayName?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                    <Text size="sm" fw={500} visibleFrom="sm">
                      {user?.displayName || 'User'}
                    </Text>
                    <IconChevronDown size={16} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item disabled>
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>{user?.displayName}</Text>
                    <Text size="xs" c="dimmed">{user?.email}</Text>
                  </Stack>
                </Menu.Item>
                <Menu.Divider />
                {/* Backend-driven user menu items from Setting YAML + NVO */}
                {userMenuItems.map((item) => {
                  const ItemIcon = getIcon(item.icon);
                  return (
                    <Menu.Item
                      key={item.id}
                      leftSection={<ItemIcon size={14} />}
                      onClick={() => navigate(item.path)}
                    >
                      {item.label}
                    </Menu.Item>
                  );
                })}
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={14} />}
                  onClick={logout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Sidebar */}
      <AppShell.Navbar p={getNavPadding(!!quickCreate, desktopCollapsed)}>
        {quickCreate ? (
          // Two-column create mode (desktop): icon-strip left + create form right
          <Box visibleFrom="sm" style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* Left: icon-only nav strip (68px) */}
            <ScrollArea style={{ width: 68, flexShrink: 0, borderRight: '1px solid var(--mantine-color-default-border)' }}>
              <Stack gap={8} align="center" py="xs" px={4}>
                <CollapsedNavItems items={topLevelItems} childrenByParent={childrenByParent} activePath={location.pathname} configLoading={configLoading} navigate={navigate} />
              </Stack>
            </ScrollArea>

            {/* Right: entity create form (fills remaining width) */}
            <Box style={{ flex: 1, overflow: 'hidden' }}>
              <SidebarEntityCreatePanel
                subgraph={quickCreate.subgraph}
                entityType={quickCreate.entityType}
                entityLabel={quickCreate.label}
                onClose={() => setQuickCreate(null)}
                onSuccess={() => setQuickCreate(null)}
              />
            </Box>
          </Box>
        ) : (
          <>
            {/* Desktop collapse toggle */}
            <AppShell.Section visibleFrom="sm" mb={4}>
              <Group justify={desktopCollapsed ? 'center' : 'flex-end'}>
                <ActionIcon variant="subtle" onClick={toggleDesktop} size="sm" aria-label="Toggle sidebar">
                  {desktopCollapsed
                    ? <IconLayoutSidebarLeftExpand size={18} />
                    : <IconLayoutSidebarLeftCollapse size={18} />
                  }
                </ActionIcon>
              </Group>
            </AppShell.Section>

            <AppShell.Section grow component={ScrollArea}>
              {/* Mobile: always full navigation */}
              <Box hiddenFrom="sm">
                <Stack gap={4}>
                  <FullNavItems items={topLevelItems} childrenByParent={childrenByParent} activePath={location.pathname} configLoading={configLoading} quickCreateEntityType={activeQuickCreateType} setQuickCreate={setQuickCreate} navigate={navigate} />
                </Stack>
              </Box>

              {/* Desktop: collapsed (icon-only) or expanded */}
              <Box visibleFrom="sm">
                {desktopCollapsed ? (
                  <Stack gap={8} align="center">
                    <CollapsedNavItems items={topLevelItems} childrenByParent={childrenByParent} activePath={location.pathname} configLoading={configLoading} navigate={navigate} />
                  </Stack>
                ) : (
                  <Stack gap={4}>
                    <FullNavItems items={topLevelItems} childrenByParent={childrenByParent} activePath={location.pathname} configLoading={configLoading} quickCreateEntityType={activeQuickCreateType} setQuickCreate={setQuickCreate} navigate={navigate} />
                  </Stack>
                )}
              </Box>
            </AppShell.Section>
          </>
        )}

        {/* Full nav drawer trigger — pinned bottom-left */}
        <AppShell.Section>
          <Group justify={desktopCollapsed ? 'center' : 'flex-start'} px={desktopCollapsed ? 0 : 4} py={4}>
            <Tooltip label="Full navigation" position="right" withArrow>
              <ActionIcon variant="subtle" size="sm" aria-label="Show full navigation" onClick={openNavDrawer}>
                <IconSitemap size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </AppShell.Section>

        {/* Safety Level Indicator */}
        {tenant?.safetyLevel && (
          <AppShell.Section>
            <Divider my="sm" />
            <Box hiddenFrom="sm">
              <Badge color={safetyColor} variant="light" fullWidth>
                Safety Level: {tenant.safetyLevel}
              </Badge>
            </Box>
            <Box visibleFrom="sm">
              {desktopCollapsed ? (
                <Tooltip label={`Safety: ${tenant.safetyLevel}`} position="right" withArrow>
                  <Badge color={safetyColor} variant="dot" size="lg" fullWidth style={{ cursor: 'default' }} />
                </Tooltip>
              ) : (
                <Badge color={safetyColor} variant="light" fullWidth>
                  Safety Level: {tenant.safetyLevel}
                </Badge>
              )}
            </Box>
          </AppShell.Section>
        )}
      </AppShell.Navbar>

      {/* Main Content */}
      <AppShell.Main>
        <FocusContextProvider>
          <FocusBanner />
          <ContextBanner />
          {children}
        </FocusContextProvider>
      </AppShell.Main>

      {/* Full nav hierarchy drawer */}
      <FullNavDrawer
        opened={navDrawerOpen}
        onClose={closeNavDrawer}
        items={topLevelItems}
        childrenByParent={childrenByParent}
        activePath={location.pathname}
        navigate={navigate}
      />

      {/* Global Search Palette (Cmd+K) - renders as overlay, not visible by default */}
      <GlobalSearchPalette />

      {/* Floating Workflow Orchestration Menu (Ctrl+O) - fixed bottom bar */}
      <FloatingOrchestrationMenu position="bottom" />

      {/* Dev Impersonation Panel (Ctrl+D) - acceptance testing rig */}
      {isDevModeEnabled() && <DevImpersonationPanel />}

      {/* Permission Debug Panel (Ctrl+Shift+D) - shows missing scopes/blocked items */}
      <PermissionDebugPanel />

      {/* Auth Debug Panel (Ctrl+Shift+A) - JWT claims, view layer, scopes */}
      <AuthDebugPanel />

      {/* Scope Gaps Panel (Ctrl+Shift+G) - DRR-284 prerequisite-blocked scopes */}
      <ScopeGapsPanel />

      {/* View Context Panel (Ctrl+Shift+V) - DRR-288 unified view context */}
      {isDevModeEnabled() && <ViewContextPanel />}

      {/* AI Panels — DRR-187 (intake) + DRR-131 (executive assistant) */}
      <AgentAssistPanel opened={agentAssistOpen} onClose={closeAgentAssist} />
      <AssistantChatPanel opened={assistantChatOpen} onClose={closeAssistantChat} onOpenIntake={openAgentAssist} />
    </AppShell>
  );
}
